/**
 * use-purchase-flow — shared SKR purchase flow.
 *
 * Provides:
 *  - `skrBalance`: live SKR balance (in whole SKR units) for the connected user.
 *  - `handlePurchase(wallpaper)`: runs balance/auth checks, then executes the purchase
 *    directly (the wallet's own confirmation prompt is the only confirmation step).
 *  - `executePurchase(wallpaper)`: actually runs the onchain burn + backend record.
 *  - `purchasingId`: id of the wallpaper currently being purchased, or null.
 *
 * The full `usePurchaseFlow` hook is used by UserProfilePage. HomePage has its own local
 * purchase handlers (a pre-existing, un-migrated duplicate — out of scope to consolidate
 * here) but imports the exported `setPurchaseAndCaptureSignature` helper below so both
 * call sites capture and submit the on-chain transaction signature identically.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth, getIdToken } from '@pooflabs/web';
import { Address, Time, set } from '@/lib/db-client';
import { buildPurchases, type PurchasesRequest } from '@/lib/collections/purchases';
import { runTokenBalanceQueryForCommonQueries } from '@/lib/collections/commonQueries';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { SKR_PRICE_RAW, SKR_TOKEN_MINT } from '@/lib/constants';
import { haptic } from '@/utils/haptic';
import { notify } from '@/utils/notifications';
import { useRingtonePlayer } from '@/contexts/RingtonePlayerContext';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';

/**
 * Creates the onchain passthrough purchase doc via the raw `set()` transport (instead of
 * the generated `setPurchases()` wrapper) so we can capture the real Solana transaction
 * signature the transport returns as `transactionId` on successful onchain writes. The
 * generated wrapper discards this by coercing its result to a boolean, but the backend now
 * requires the signature to verify the payment on-chain before granting library access
 * (see /api/confirm-purchase). Mirrors setPurchases()'s own try/catch-and-log behavior so a
 * policy denial or insufficient balance still resolves to `{ ok: false }` instead of throwing.
 */
export async function setPurchaseAndCaptureSignature(
  purchaseId: string,
  data: PurchasesRequest,
): Promise<{ ok: boolean; txSignature?: string }> {
  try {
    const doc = buildPurchases(purchaseId, data);
    const result = await set(doc.path, doc.document);
    const txSignature =
      result && typeof result === 'object' && typeof (result as { transactionId?: unknown }).transactionId === 'string'
        ? ((result as { transactionId: string }).transactionId || undefined)
        : undefined;
    return { ok: !!result, txSignature };
  } catch (error) {
    console.error('Error setting Purchases:', error);
    return { ok: false };
  }
}

function useSkrBalance(userAddress: string | undefined, refreshKey: number) {
  const [balance, setBalance] = useState<number | null>(null);
  useEffect(() => {
    if (!userAddress) {
      setBalance(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const raw = await runTokenBalanceQueryForCommonQueries('balance-check', {
          walletAddress: userAddress,
          tokenMint: SKR_TOKEN_MINT,
        });
        if (!cancelled) setBalance(raw / 1_000_000);
      } catch {
        if (!cancelled) setBalance(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userAddress, refreshKey]);
  return balance;
}

function resolveMediaType(w: WallpapersResponse): string {
  const stored = (w as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored) return stored;
  const url = (w.imageUrl || '').toLowerCase().split('?')[0];
  if (/\.(mp4|webm|mov|m4v)$/.test(url)) return 'live-wallpaper';
  if (/\.(mp3|wav|ogg|m4a|aac)$/.test(url)) return 'ringtone';
  return 'wallpaper';
}

export interface UsePurchaseFlowOptions {
  /** Set of wallpaper ids the current user already owns (used to skip re-purchase). */
  purchasedIds?: Set<string>;
}

export interface UsePurchaseFlowResult {
  skrBalance: number | null;
  purchasingId: string | null;
  handlePurchase: (wallpaper: WallpapersResponse) => Promise<void>;
}

export function usePurchaseFlow({ purchasedIds }: UsePurchaseFlowOptions = {}): UsePurchaseFlowResult {
  const { user } = useAuth();
  const ringtonePlayer = useRingtonePlayer();

  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [balanceRefreshKey, setBalanceRefreshKey] = useState(0);
  const skrBalance = useSkrBalance(user?.address, balanceRefreshKey);

  const executePurchase = useCallback(async (wallpaper: WallpapersResponse) => {
    if (!user) return;
    setPurchasingId(wallpaper.id);
    setBalanceRefreshKey(k => k + 1);

    const PURCHASE_TIMEOUT_MS = 90_000;
    const purchaseTimeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('PURCHASE_TIMEOUT')), PURCHASE_TIMEOUT_MS),
    );

    try {
      const purchaseId = crypto.randomUUID();
      const purchaseAmt = wallpaper.price ?? Number(SKR_PRICE_RAW);
      const purchaseResult = await Promise.race([
        setPurchaseAndCaptureSignature(purchaseId, {
          buyer: Address.publicKey(user.address),
          wallpaperId: wallpaper.id,
          amt: purchaseAmt,
          createdAt: Time.Now,
          creator: Address.publicKey(wallpaper.uploadedBy),
        }),
        purchaseTimeoutPromise,
      ]);

      if (!purchaseResult.ok) {
        console.error('setPurchases failed — policy denial or insufficient SKR balance');
        haptic.error();
        notify.error(
          'Purchase failed. The wallpaper price may be invalid or your balance is too low — please try again.',
          user?.address,
        );
        return;
      }

      if (!purchaseResult.txSignature) {
        console.error('Onchain purchase succeeded but no transaction signature was returned — cannot confirm purchase');
        haptic.error();
        notify.error('Purchase could not be confirmed. Please contact support if SKR was deducted.', user?.address);
        return;
      }

      const token = await getIdToken();
      if (!token) {
        haptic.error();
        notify.error('Authentication required', user?.address);
        return;
      }

      const authApi = createAuthenticatedApiClient(token, user.address);
      const result = await authApi.post('/api/confirm-purchase', {
        wallpaperId: wallpaper.id,
        txSignature: purchaseResult.txSignature,
      });

      const mediaType = resolveMediaType(wallpaper);
      setBalanceRefreshKey(k => k + 1);
      if (!result?.confirmed) {
        console.warn('User purchase record failed, but purchase succeeded');
      }
      // Optimistic update for the ringtone full-screen player.
      if (mediaType === 'ringtone' && ringtonePlayer.currentRingtone?.id === wallpaper.id) {
        ringtonePlayer.setPurchased(true);
      }
    } catch (error: unknown) {
      console.error('Purchase error:', error);
      if (error instanceof Error && error.message === 'PURCHASE_TIMEOUT') {
        haptic.error();
        notify.error(
          'Purchase timed out. Make sure your wallet is connected to this site, then try again.',
          user?.address,
        );
        return;
      }
      const isUserRejection = (() => {
        if (!error || typeof error !== 'object') return false;
        const e = error as Record<string, unknown>;
        if (e.code === 4001) return true;
        if (e.name === 'UserRejectedRequestError') return true;
        const msg = typeof e.message === 'string' ? e.message.toLowerCase() : '';
        return (
          msg.includes('user rejected') ||
          msg.includes('rejected the request') ||
          msg.includes('user declined') ||
          msg.includes('cancelled') ||
          msg.includes('canceled')
        );
      })();
      if (isUserRejection) return;
      haptic.error();
      notify.error('Purchase failed. Please try again.', user?.address);
    } finally {
      setPurchasingId(null);
    }
  }, [user, ringtonePlayer]);

  const handlePurchase = useCallback(async (wallpaper: WallpapersResponse) => {
    if (!user) {
      haptic.error();
      notify.error('Connect your wallet to buy');
      return;
    }
    if (purchasedIds?.has(wallpaper.id)) return;
    const priceRaw = wallpaper.price ?? 1_000_000;
    if (skrBalance !== null && Math.floor(skrBalance * 1_000_000) < priceRaw) {
      haptic.error();
      notify.error('Insufficient SKR balance', user?.address);
      return;
    }
    await executePurchase(wallpaper);
  }, [user, purchasedIds, skrBalance, executePurchase]);

  return {
    skrBalance,
    purchasingId,
    handlePurchase,
  };
}
